import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import Add from '@mui/icons-material/Add';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from 'src/components/Card';
import { CircularProgress } from 'src/components/CircularProgress';
import { Dialog } from 'src/components/Dialog';
import { Grid } from 'src/components/Grid';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { SectionHeader } from 'src/components/SectionHeader';
import { TextField, TextFieldProps } from 'src/components/TextField';
import { Tooltip } from 'src/components/Tooltip';
import { externalLinks } from 'src/externalLinks';
import { StripePlan } from 'src/models/payment/_types';
import { fetchStripePlans } from 'src/models/payment/payment';
import {
  SubscriptionPlanForm,
  SubscriptionPlanFormFields,
} from 'src/shared/Forms/SubscriptionPlanForm';
import { Identifier } from 'src/types/utilityTypes';
import { formatCurrency } from 'src/utils/numberFormatter';

import { BaseSelect, BaseSelectOption } from './BaseSelect';

interface ChildrenProps {
  setShowCreatePlanDialog: React.Dispatch<React.SetStateAction<boolean>>;
}

interface PaymentPlanSelectProps extends Omit<TextFieldProps, 'onChange'> {
  userId: Identifier;
  onChange: (value: StripePlan | StripePlan[]) => void;
  excludeDestinationPlans?: boolean;
  multiple?: boolean;
  value?: string | string[];
  onCreatePlan?: (plan: Partial<StripePlan>) => void;
  children?: (props: ChildrenProps) => React.ReactNode;
  packageLinkId?: Identifier;
  hideCreateButton?: boolean;
}

export const PaymentPlanSelect: React.FC<PaymentPlanSelectProps> = ({
  userId,
  onChange,
  excludeDestinationPlans = false,
  multiple = false,
  value,
  onCreatePlan,
  children,
  packageLinkId,
  hideCreateButton = false,
  ...props
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'PaymentPlanSelect',
  });
  const [showCreatePlanDialog, setShowCreatePlanDialog] = React.useState(false);
  const [formResetKey, setFormResetKey] = React.useState(0);

  const curDomain = externalLinks.railsBase || window.location.origin;

  const { data: plans, isLoading } = useQuery({
    queryKey: ['stripePlans', userId],
    queryFn: () => fetchStripePlans(userId),
    staleTime: 60000,
    gcTime: 60000,
  });

  const queryClient = useQueryClient();

  const formatPlanLabel = (plan: StripePlan) => {
    const amount = formatCurrency(plan.amount / 100.0, {
      currency: plan.currency,
    });
    const interval =
      plan.intervalCount > 1
        ? `${plan.intervalCount} ${plan.interval}s`
        : plan.interval;

    return `${plan.name} (${amount}/${interval})`;
  };

  if (isLoading) return <TextField {...props} disabled />;

  const filteredPlans =
    plans?.filter((plan) => {
      if (!plan.active) return false;
      if (excludeDestinationPlans && plan.metadata.platformId) return false;
      return true;
    }) || [];

  const handleCreatePlan = async (plan: Partial<StripePlan>) => {
    try {
      await onCreatePlan?.(plan);
      // Only close dialog and refetch after successful creation
      setShowCreatePlanDialog(false);
      queryClient.invalidateQueries({
        queryKey: ['stripePlans', userId],
      });
      // Reset form by remounting it
      setFormResetKey((prev) => prev + 1);
    } catch (error) {
      // Handle error if needed
    }
  };

  const handleCloseDialog = () => {
    setShowCreatePlanDialog(false);
    // Reset form by remounting it
    setFormResetKey((prev) => prev + 1);
  };

  const createPlanDialog = (
    <SubscriptionPlanForm key={formResetKey} afterSave={handleCreatePlan}>
      {({ submitForm, isValid, isSubmitting }) => (
        <Dialog
          open={showCreatePlanDialog}
          title="Create Payment Plan"
          leftAdornment={
            <IconButton onClick={handleCloseDialog}>
              <Icon icon={icon({ name: 'arrow-left', style: 'solid' })} />
            </IconButton>
          }
          actions={[
            { label: 'Cancel', onClick: handleCloseDialog },
            {
              label: isSubmitting ? 'Creating...' : 'Create Plan',
              onClick: submitForm,
              disabled: !isValid || isSubmitting,
              variant: 'contained',
              color: 'primary',
              startIcon: isSubmitting ? (
                <CircularProgress size="small" />
              ) : undefined,
            },
          ]}
        >
          <SubscriptionPlanFormFields isEditing={false} />
        </Dialog>
      )}
    </SubscriptionPlanForm>
  );

  // Convert plans to BaseSelectOption format for both cases
  const baseSelectOptions: BaseSelectOption[] = filteredPlans.map((plan) => ({
    id: plan.id,
    label: formatPlanLabel(plan),
    value: plan,
  }));

  // Create a custom label with the "Create Plan" button
  const baseSelectLabel = props.label && (
    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      {props.label}
      {onCreatePlan && !hideCreateButton && (
        <Tooltip title="Create Payment Plan">
          <IconButton onClick={() => setShowCreatePlanDialog(true)}>
            <Add fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </span>
  );

  const selectedStripePlans = plans?.filter((plan) => {
    if (Array.isArray(value)) {
      return value.includes(plan.id);
    } else {
      return plan.id === value;
    }
  });

  const PackageLinks = packageLinkId && !!selectedStripePlans?.length && (
    <Card sx={{ mt: 2 }}>
      <SectionHeader title={t('paymentOptionLinks')} bottomDivider />

      <Grid container spacing={2} p={2}>
        {/* Default Checkout Link */}
        <Grid size={12}>
          <TextField
            label={t('defaultCheckoutLink')}
            fullWidth
            disabled
            value={`${curDomain}${externalLinks.fbmPackage(packageLinkId)}`}
          />
        </Grid>

        {/* Direct Links for Each Plan */}
        {selectedStripePlans?.map((plan) => (
          <Grid size={12} key={plan.id}>
            <TextField
              label={t('directLinkFor', { plan: formatPlanLabel(plan) })}
              fullWidth
              disabled
              value={`${curDomain}${externalLinks.fbmPackage(
                packageLinkId
              )}?plan_ids=${plan.id}`}
            />
          </Grid>
        ))}
      </Grid>
    </Card>
  );

  if (multiple) {
    return (
      <>
        <BaseSelect
          options={baseSelectOptions}
          value={Array.isArray(value) ? value : value ? [value] : []}
          onChange={(selectedValues) => {
            // BaseSelect with valueOnly=true returns array of ids
            if (Array.isArray(selectedValues)) {
              const selectedPlans = selectedValues
                .map((id) => filteredPlans.find((plan) => plan.id === id))
                .filter(Boolean) as StripePlan[];

              onChange(selectedPlans);
            }
          }}
          label={baseSelectLabel}
          disabled={isLoading || !!props.disabled}
          placeholder={props.placeholder || 'Select plans...'}
          multiple={true}
          valueOnly={true}
        />
        {children?.({ setShowCreatePlanDialog })}
        {PackageLinks}
        {createPlanDialog}
      </>
    );
  }

  return (
    <>
      <BaseSelect
        options={baseSelectOptions}
        value={value || null}
        onChange={(selectedValue) => {
          if (selectedValue) {
            const selectedPlan = filteredPlans.find(
              (plan) => plan.id === selectedValue
            );
            onChange(selectedPlan as StripePlan);
          } else {
            onChange(null as any);
          }
        }}
        label={baseSelectLabel}
        disabled={isLoading || !!props.disabled}
        placeholder={props.placeholder || 'Select plan...'}
        multiple={false}
        valueOnly={true}
      />
      {children?.({ setShowCreatePlanDialog })}
      {PackageLinks}
      {createPlanDialog}
    </>
  );
};
